// One place that answers a single question: where do this member's documents go?
//
// There are exactly three address ideas in the member model:
//   legal_address (as it appears on the driver's license)
//   shipping_line1 ... shipping_zip (only when documents go somewhere else)
//   the association's California address, handled by the suite machinery
//
// When the member gave a separate shipping address, that one wins. Otherwise
// documents follow the legal address.

import { AddressValue, toAddress } from "@/components/onboarding/AddressFields";
import { PLATFORM_ADDRESS_TEMPLATE } from "@/config/integration-placeholders";

export type ShippingRecord = {
  legal_address?: unknown;
  shipping_line1?: string | null;
  shipping_line2?: string | null;
  shipping_city?: string | null;
  shipping_state?: string | null;
  shipping_zip?: string | null;
};

/** The member's legal address. */
export function legalAddressOf(record: ShippingRecord | null | undefined): AddressValue {
  return toAddress(record?.legal_address ?? null);
}

/** The shipping address as stored in its own columns. */
export function shippingAddressOf(record: ShippingRecord | null | undefined): AddressValue {
  return toAddress({
    line1: record?.shipping_line1 ?? "",
    line2: record?.shipping_line2 ?? "",
    city: record?.shipping_city ?? "",
    state: record?.shipping_state ?? "",
    zip: record?.shipping_zip ?? "",
  });
}

/** True when the member asked us to ship somewhere other than their mailing address. */
export function hasSeparateShipping(record: ShippingRecord | null | undefined): boolean {
  return Boolean(record?.shipping_line1 && String(record.shipping_line1).trim());
}

/** Where the documents actually ship. */
export function shippingDestination(record: ShippingRecord | null | undefined): {
  address: AddressValue;
  isSeparate: boolean;
} {
  if (hasSeparateShipping(record)) {
    return { address: shippingAddressOf(record), isSeparate: true };
  }
  return { address: legalAddressOf(record), isSeparate: false };
}

/** Display lines for any address, empty parts dropped. */
export function addressLines(a: AddressValue): string[] {
  return [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(", "), a.zip].filter(
    (part) => Boolean(part && part.trim()),
  ) as string[];
}

/** One line version, handy for cards and tables. */
export function addressOneLine(a: AddressValue): string {
  return addressLines(a).join(", ");
}

/**
 * The association's registered address. When a suite is assigned we compose the
 * platform address; otherwise we follow the member's California address choice.
 */
export function unaAddressDisplay(record: {
  suite_number?: number | null;
  ca_address_option?: string | null;
  ca_address?: unknown;
} | null | undefined): { lines: string[]; note: string } {
  if (record?.suite_number != null) {
    return {
      lines: [PLATFORM_ADDRESS_TEMPLATE.replace("{N}", String(record.suite_number))],
      note: "",
    };
  }
  const own = addressLines(toAddress(record?.ca_address ?? null));
  if (own.length) return { lines: own, note: "" };

  if (record?.ca_address_option === "self_sourced") {
    return { lines: [], note: "Bringing their own (pending)" };
  }
  return { lines: [], note: "Not settled yet" };
}
